import {
  startAuthentication,
  type PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";
import { authClient } from "./authClient";

type AuthenticateOptions = PublicKeyCredentialRequestOptionsJSON & {
  hints?: Array<"client-device" | "hybrid" | "security-key">;
};

/**
 * Passkey sign-in that prefers the platform authenticator (Touch ID / Face ID /
 * Windows Hello) via WebAuthn hints. Better Auth's default client leaves hints
 * unset, so Chrome often opens the phone-QR / security-key sheet instead.
 */
export const signInWithPlatformPasskey = async (): Promise<{
  ok: true;
} | {
  ok: false;
  cancelled: boolean;
}> => {
  const options = await authClient.$fetch<AuthenticateOptions>(
    "/passkey/generate-authenticate-options",
    { method: "GET" },
  );
  if (options.error || !options.data) {
    return { ok: false, cancelled: false };
  }

  try {
    const assertion = await startAuthentication({
      optionsJSON: {
        ...options.data,
        hints: ["client-device"],
      },
    });
    const verified = await authClient.$fetch("/passkey/verify-authentication", {
      method: "POST",
      body: { response: assertion },
    });
    if (verified.error) {
      return { ok: false, cancelled: false };
    }
    return { ok: true };
  } catch (err) {
    const name = err instanceof Error ? err.name : "";
    const cancelled =
      name === "NotAllowedError" ||
      (err instanceof Error && /cancel|abort/i.test(err.message));
    return { ok: false, cancelled };
  }
};
